import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useRouteLoaderData } from 'react-router-dom';
import useApiMutation from '@/hooks/useApiMutation';
import useFormData from '@/hooks/useFormData';
import { Delete } from '@mui/icons-material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { QRCodeSVG } from 'qrcode.react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useTheme, useMediaQuery } from '@mui/material';
import { toast } from 'react-toastify';
import { ContentCopy } from '@mui/icons-material';
import api from '@/services/ApiService';
import dayjs from 'dayjs';

export default function ToolForm({ data: tool }) {
  const { users, me } = useRouteLoaderData('auth');
  const { mutateAsync: updateTool } = useApiMutation('tool', tool?._id);
  const { formData, reset } = useFormData(tool);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [events, setEvents] = useState([]);

  const hasReservation = Array.isArray(events) && events.length > 0;
  const [forceSave, setForceSave] = useState(false);

  const formatTime = (d) => dayjs(d).format('DD.MM.YYYY HH:mm');

  useEffect(() => {
    let isActive = true;

    async function loadEvents() {
      try {
        if (!tool?._id) return;

        const now = new Date();
        const horizon = new Date(now);
        horizon.setDate(horizon.getDate() + 1);

        const params =
          `tools=${tool._id}` +
          `&end[$gt]=${encodeURIComponent(now.toISOString())}` +
          `&start[$lt]=${encodeURIComponent(horizon.toISOString())}`;

        const {
          data: { result: items = [] },
        } = await api.get(`/event?${params}`);

        if (!isActive) return;
        setEvents(items);
      } catch (err) {
        console.error('Events laden fehlgeschlagen:', err);
        if (!isActive) return;
        setEvents([]);
      }
    }

    loadEvents();
    return () => {
      isActive = false;
    };
  }, [tool?._id]);

  const [rows, setRows] = useState(() =>
    (tool.items || []).map((item) => ({
      ...item,
      id: item.id || `ITEM-${Math.random().toString(36).substr(2, 16)}`,
      quantity: Number(item.count) || 1,
    })),
  );

  const [localFormData, setLocalFormData] = useState({
    ...formData,
    comment: '',
  });

  const apiRef = useRef();

  const handleAddTool = () => {
    const newRow = {
      id: `ITEM-${Math.random().toString(36).substr(2, 16)}`,
      quantity: 1,
      count: 1,
      title: '',
      description: '',
    };

    setRows((prev) => [...prev, newRow]);
    setLocalFormData({
      ...localFormData,
      items: [...(localFormData.items || []), newRow],
    });

    setTimeout(() => {
      if (apiRef.current) {
        apiRef.current.startRowEditMode({
          id: newRow.id,
          fieldToFocus: 'title',
        });
      }
    }, 50);
  };

  const columns = [
    { field: 'title', headerName: 'Titel', flex: 1, editable: true },
    {
      field: 'quantity',
      headerName: 'Menge',
      flex: 1,
      editable: true,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      renderCell: ({ row }) => (
        <IconButton
          color="disabled"
          onClick={() => {
            setRows(rows.filter((r) => r.id !== row.id));
            setLocalFormData({
              ...localFormData,
              items: (localFormData.items || []).filter((i) => i.id !== row.id),
            });
          }}
        >
          <Delete />
        </IconButton>
      ),
    },
  ];

  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.up('sm'));

  const reservationOwnerIds = useMemo(() => {
    const ids = new Set();
    for (const ev of events || []) {
      const candidates = [ev?.reservedBy, ev?.owner, ev?.user];
      candidates.forEach((c) => {
        const id = typeof c === 'string' ? c : c?._id || c?.id;
        if (id) ids.add(id);
      });

      const arrays = [ev?.users];
      arrays.forEach((arr) => {
        if (Array.isArray(arr)) {
          arr.forEach((p) => {
            const id = typeof p === 'string' ? p : p?._id || p?.id;
            if (id) ids.add(id);
          });
        }
      });
    }
    return ids;
  }, [events]);

  const isReservedBySelectedTechnician =
    !!localFormData.takenBy && reservationOwnerIds.has(localFormData.takenBy);

  const saveBlockedByReservation =
    hasReservation &&
    localFormData.status === 'TAKEN' &&
    !isReservedBySelectedTechnician;

  useEffect(() => {
    if (!saveBlockedByReservation) setForceSave(false);
  }, [saveBlockedByReservation, localFormData.status, localFormData.takenBy]);

  const reservationUsers = useMemo(() => {
    try {
      return (users || []).filter((u) => reservationOwnerIds.has(u._id));
    } catch {
      return [];
    }
  }, [users, reservationOwnerIds]);

  const reservationUserNameById = useMemo(
    () =>
      Object.fromEntries(
        (reservationUsers || []).map((u) => [
          u._id,
          `${u.firstName || ''} ${u.lastName || ''}`.trim(),
        ]),
      ),
    [reservationUsers],
  );

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <TextField
          label="Bezeichnung"
          value={localFormData.title || ''}
          onChange={(e) =>
            setLocalFormData({ ...localFormData, title: e.target.value })
          }
          variant="outlined"
          size="small"
          sx={{ flex: 1 }}
        />

        <Tooltip title={sm ? 'NFC-Chip beschreiben' : 'URL kopieren'}>
          <Button
            variant="outlined"
            onClick={() => {
              if (sm) {
                setQrDialogOpen(true);
              } else {
                navigator.clipboard.writeText(
                  `${import.meta.env.VITE_LIVE_URL}tool/${tool._id}`,
                );
                toast.success('URL kopiert!', {
                  position: 'bottom-right',
                  autoClose: 1500,
                });
              }
            }}
            sx={{
              minWidth: 0,
              height: '94%',
              padding: sm ? 1 : '8px 10px',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            {sm && 'QR-Code'}
            {sm ? <QrCodeScannerIcon /> : <ContentCopy />}
          </Button>
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: sm ? 'row' : 'column',
          gap: 2,
          alignItems: sm ? 'center' : 'stretch',
          justifyContent: sm ? 'space-between' : 'flex-start',
        }}
      >
        <TextField
          select
          label="Techniker"
          value={localFormData.takenBy || ''}
          onChange={(e) =>
            setLocalFormData({
              ...localFormData,
              takenBy: e.target.value || null,
            })
          }
          SelectProps={{ native: true }}
          size="small"
          sx={{ width: sm ? 200 : '100%' }}
          InputLabelProps={{ shrink: true }}
        >
          <option value="">kein Techniker</option>
          {users.map((user) => (
            <option key={user._id} value={user._id}>
              {user.firstName} {user.lastName}
            </option>
          ))}
        </TextField>

        <TextField
          select
          label="Status"
          value={localFormData.status || 'AVAILABLE'}
          onChange={(e) => {
            const newStatus = e.target.value;

            setLocalFormData((prev) => {
              const next = {
                ...prev,
                status: newStatus,
                toolStatus:
                  newStatus === 'AVAILABLE' || newStatus === 'TAKEN'
                    ? 'OK'
                    : newStatus === 'MAINTENANCE'
                      ? 'MISSING'
                      : prev.toolStatus,
              };

              if (newStatus === 'TAKEN') {
                if (!prev.takenBy && me?._id) {
                  next.takenBy = me._id;
                }
                next.takenAt = prev.takenAt || new Date().toISOString();
                next.returnedAt = null;
              } else if (newStatus === 'AVAILABLE') {
                next.takenBy = null;
                next.returnedAt = prev.returnedAt || new Date().toISOString();
              }

              return next;
            });
          }}
          SelectProps={{ native: true }}
          size="small"
          sx={{ width: sm ? 200 : '100%' }}
          InputLabelProps={{ shrink: true }}
        >
          <option value="AVAILABLE">Verfügbar im Lager</option>
          <option value="TAKEN">Mitgenommen</option>
          <option value="MAINTENANCE">Nicht verfügbar</option>
        </TextField>

        <TextField
          select
          label="Zustand"
          value={localFormData.toolStatus || 'OK'}
          onChange={(e) => {
            const newToolStatus = e.target.value;
            setLocalFormData({
              ...localFormData,
              toolStatus: newToolStatus,
              status:
                newToolStatus === 'BROKEN' || newToolStatus === 'MISSING'
                  ? 'MAINTENANCE'
                  : newToolStatus === 'OK'
                    ? 'AVAILABLE'
                    : localFormData.status,
            });
          }}
          SelectProps={{ native: true }}
          size="small"
          sx={{ width: sm ? 200 : '100%' }}
          InputLabelProps={{ shrink: true }}
        >
          <option value="BROKEN">Defekt</option>
          <option value="MISSING">Fehlt</option>
          <option value="OK">Funktionstüchtig</option>
        </TextField>
      </Box>

      <Button variant="outlined" color="primary" onClick={handleAddTool}>
        Hinzufügen
      </Button>

      <Box sx={{ minHeight: '200px', flex: 1, overflow: 'auto' }}>
        <DataGridPro
          apiRef={apiRef}
          editMode="row"
          rows={rows}
          columns={columns}
          checkboxSelection
          disableRowSelectionOnClick
          pagination={false}
          getRowId={(row) => row.id}
          processRowUpdate={(updatedRow) => {
            const updatedItems = rows.map((item) =>
              item.id === updatedRow.id
                ? {
                    ...item,
                    title: updatedRow.title,
                    count: Number(updatedRow.quantity),
                    quantity: Number(updatedRow.quantity),
                  }
                : item,
            );
            setRows(updatedItems);
            setLocalFormData({ ...localFormData, items: updatedItems });
            return { ...updatedRow, quantity: Number(updatedRow.quantity) };
          }}
          experimentalFeatures={{ newEditingApi: true }}
          sx={{ minHeight: 200 }}
        />
      </Box>

      <TextField
        label="Kommentar"
        value={localFormData.comment || ''}
        onChange={(e) =>
          setLocalFormData({ ...localFormData, comment: e.target.value })
        }
        multiline
        rows={2}
        fullWidth
      />

      {hasReservation && (
        <Alert severity="warning">
          <Typography sx={{ fontWeight: 500 }}>
            Dieses Werkzeug wurde bereits reserviert von:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {(events || [])
              .filter((ev) => ev?.start && ev?.end)
              .sort((a, b) => new Date(a.start) - new Date(b.start))
              .slice(0, 3)
              .map((ev) => {
                const raw = ev?.reservedBy ?? ev?.owner ?? ev?.user;
                const userId =
                  typeof raw === 'string' ? raw : raw?._id || raw?.id;

                const name = reservationUserNameById[userId] || 'Unbekannt';

                return (
                  <Typography key={ev._id || ev.id} variant="body2">
                    {name} ({formatTime(ev.start)} – {formatTime(ev.end)})
                  </Typography>
                );
              })}
          </Box>

          {events.length > 3 && (
            <Typography variant="caption" color="text.secondary">
              +{events.length - 3} weitere Reservierung(en)
            </Typography>
          )}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 2, mt: 'auto' }}>
        <Button
          variant="outlined"
          onClick={() => {
            reset();
            setLocalFormData(formData);
            setRows(
              (tool.items || []).map((item) => ({
                ...item,
                id:
                  item.id || `ITEM-${Math.random().toString(36).substr(2, 16)}`,
                quantity: Number(item.count) || 1,
              })),
            );
          }}
        >
          Verwerfen
        </Button>

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          color={saveBlockedByReservation && !forceSave ? 'error' : 'primary'}
          onClick={async () => {
            const payload = {
              title: localFormData.title || '',
              toolStatus: localFormData.toolStatus || 'OK',
              status: localFormData.status || 'AVAILABLE',
              takenBy: localFormData.takenBy || null,
              takenAt:
                localFormData.status === 'TAKEN'
                  ? localFormData.takenAt || new Date().toISOString()
                  : null,
              returnedAt:
                localFormData.status === 'AVAILABLE'
                  ? localFormData.returnedAt || new Date().toISOString()
                  : null,
              items: rows.map((r) => ({
                title: r.title || '',
                description: r.description || '',
                count: Number(r.quantity) || 1,
              })),
              content: localFormData.comment || formData.content || '',
            };

            await updateTool(payload);
            setLocalFormData((prev) => ({ ...prev, comment: '' }));
            setForceSave(false);
          }}
        >
          {saveBlockedByReservation && !forceSave
            ? 'Trotzdem speichern'
            : 'Speichern'}
        </Button>
      </Box>

      <Dialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', m: 2 }}>
          <Typography variant="h4">QR Code für Werkzeug</Typography>
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 4,
          }}
        >
          {tool?._id && (
            <QRCodeSVG
              value={`${import.meta.env.VITE_LIVE_URL}tool/${tool._id}`}
              size={256}
            />
          )}

          <Typography variant="subtitle2" sx={{ mt: 2, textAlign: 'center' }}>
            Scanne den QR-Code, kopiere die URL und beschreibe anschließend den
            NFC-Chip.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)} variant="contained">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
